import { NavigationItemModel } from './Header.types';

/**
 * Check if the item's id matches the active item's id
 * @param itemId item id from map
 * @param activeItemId active item id from state
 * @returns whether the item's id matches the active item's id
 */
export function idMatchesActiveItemId(itemId: string, activeItemId: string | null): boolean {
  if (activeItemId === null) {
    return false;
  }

  const activeIdParts = activeItemId.split('/');
  const itemParts = itemId.split('/');

  // Check if the item's id starts with the activeId and the immediate child matches
  const matches =
    itemParts.length > 1 &&
    ((itemParts[0] === activeIdParts[0] && itemParts[1] === activeIdParts[1]) ||
      (itemParts[0] === activeIdParts[0] && itemParts[1] === activeIdParts[1] && itemParts[2] === activeIdParts[2]));

  return matches;
}

/**
 * @description Check if the segments of the item ID match the segments of the active item ID
 */
function itemSegmentsMatchesActiveItemSegments(itemIdSegments: string[], activeItemIdSegments: string[]): boolean {
  const matches = activeItemIdSegments.every((x, i) => x === itemIdSegments[i]);

  return matches;
}

/**
 * @description Check if the dropdown column level is visible based on segments of the active item ID
 */
export function columnVisible(level: NavigationItemModel[] | undefined, activeItemId: string | null): boolean {
  if (!level || !activeItemId) {
    return false;
  }

  // Split the active item ID into segments
  const activeItemIdSegments = activeItemId.split('/');
  const activeItemIdSegmentLength = activeItemIdSegments.length;

  const levelMatchesActiveItemId = level.filter((x) => {
    // Split the item ID into segments
    const itemIdSegments = x.id.split('/');
    const itemIdSegmentsLength = itemIdSegments.length;

    return (
      // Only show up to the next level
      itemIdSegmentsLength <= activeItemIdSegmentLength + 1 &&
      // Check all segments of the item ID match segments of the active ID up to the length of the item ID
      itemSegmentsMatchesActiveItemSegments(itemIdSegments, activeItemIdSegments.slice(0, itemIdSegmentsLength))
    );
  });

  return levelMatchesActiveItemId.length > 0;
}

/**
 * @description Check if an item is active based on the segments of the active item ID
 **/
export function isActiveItem(itemId: string, activeItemId: string | null): boolean {
  if (activeItemId === null) {
    return false;
  }

  // Split the active item ID and the item ID into segments
  const activeItemIdSegments = activeItemId.split('/');
  const itemIdSegments = itemId.split('/');

  // Store the lengths of the segment arrays to avoid redundant calculations
  const itemIdSegmentsLength = itemIdSegments.length;
  const activeItemIdSegmentLength = activeItemIdSegments.length;

  // Check if the item ID is a prefix of the active item ID and the last segment matches
  return (
    // Only show up to the active level
    itemIdSegmentsLength <= activeItemIdSegmentLength &&
    // Check if the item ID is a prefix of the active item ID
    activeItemId.startsWith(itemId) &&
    // Check if the last segment of the item ID matches the last segment of the active item ID
    itemIdSegments[itemIdSegmentsLength - 1] === activeItemIdSegments[itemIdSegmentsLength - 1]
  );
}

/**
 * @description Check if a link is visible based on the segments of the active item ID
 */
export function linkVisible(itemId: string, activeItemId: string | null): boolean {
  if (activeItemId === null) {
    return false;
  }

  // Split the active item ID and the item ID into segments
  const activeItemIdSegments = activeItemId.split('/');
  const itemIdSegments = itemId.split('/');

  // Store the lengths of the segment arrays to avoid redundant calculations
  const itemIdSegmentsLength = itemIdSegments.length;
  const activeItemIdSegmentLength = activeItemIdSegments.length;

  // Check different cases to determine if the link is visible
  return (
    // Case 1: Item is a child
    (itemIdSegmentsLength === activeItemIdSegmentLength + 1 && itemId.startsWith(activeItemId)) ||
    // Case 2: Item is a parent
    (itemIdSegmentsLength <= activeItemIdSegmentLength && activeItemId.startsWith(itemId)) ||
    // Case 3: Item is a sibling
    (itemIdSegmentsLength === activeItemIdSegmentLength &&
      itemId.startsWith(activeItemIdSegments.slice(0, -1).join('/'))) ||
    // Case 4: Item is a cousin
    itemId.startsWith(activeItemIdSegments.slice(0, activeItemIdSegmentLength - 2).join('/')) ||
    // Case 5: Item is a grandchild
    itemId.startsWith(activeItemIdSegments.slice(0, activeItemIdSegmentLength - 3).join('/'))
  );
}
